// Lógica pura sobre los datos de una versión: sin React ni llamadas a la API, para poder testearla sola.

// El backend manda cada publicación sólo con el nombre del archivo; acá se le cuelga el archivo y se arman
// conjuntos por cliente (en minúsculas) para consultar en O(1) desde cada fila. `reason` es el motivo de un ignorado.
// Un update va una sola vez por cliente: las filas se identifican por nombre, y un repetido descuadra lo tildado.
const withoutRepeats = (pubs) => { const seen = new Set(); return pubs.filter(p => !seen.has(p.name) && seen.add(p.name)); };

export const normalizeFiles = (data) => {
    const all_updates = data.all_updates || [];
    const all_files_map = Object.fromEntries(all_updates.map(f => [f.name, f]));
    const hydrate = (groups) => Object.fromEntries(Object.entries(groups || {}).map(([client, pubs]) =>
        [client, withoutRepeats(pubs).map(p => ({ file: all_files_map[p.name], timestamp: p.timestamp, hash_at_time: p.hash_at_time || all_files_map[p.name]?.hash, reason: p.reason || '', downloads: p.downloads || 0, downloaded_at: p.downloaded_at || null })).filter(p => p.file)]));
    const nameSets = (groups) => Object.fromEntries(Object.entries(groups).map(([client, pubs]) => [client, new Set(pubs.map(p => p.file.name.toLowerCase()))]));
    const published = hydrate(data.published);
    const ignored = hydrate(data.ignored);
    return { all_updates, all_files_map, published, ignored, published_names: nameSets(published), ignored_names: nameSets(ignored), outdated_checked_at: data.outdated_checked_at || null };
};

export const EMPTY_FILES = normalizeFiles({});

// Los listados que la app guarda en memoria: una lista de [versión, listado] del más viejo al más nuevo (una lista y
// no un objeto, que ordenaría por su cuenta los nombres que son números). El que se acaba de cargar reemplaza al suyo y
// queda de último, y de los demás se conservan los más recientes hasta que sean `limit` en total.
export const keepListing = (kept, version, data, limit) => {
    const others = kept.filter(([name]) => name !== version);
    return [...others.slice(Math.max(0, others.length - (limit - 1))), [version, data]];
};
export const keptListing = (kept, version) => kept.find(([name]) => name === version)?.[1];

// Lo desactualizado de todas las versiones (`GET /api/outdated`), con cada update una sola vez, el más nuevo primero:
// en qué versiones está desactualizado, con qué clientes en cada una, y todos sus clientes juntos.
export const mergeOutdated = (listing) => {
    const byName = new Map();
    listing.forEach(({ version, updates }) => updates.forEach(({ name, clients, uploaded_by_admin }) => {
        if (!byName.has(name)) byName.set(name, { name, versions: [] });
        byName.get(name).versions.push({ version, clients, uploaded_by_admin: !!uploaded_by_admin });
    }));
    return [...byName.values()].sort((a, b) => (a.name < b.name ? 1 : -1))
        .map(item => ({ ...item, clients: [...new Set(item.versions.flatMap(entry => entry.clients))].sort(), uploaded_by_admin: item.versions.some(entry => entry.uploaded_by_admin) }));
};

export const isPublished = (files, client, name) => files.published_names[client]?.has(name.toLowerCase()) || false;
export const isIgnored = (files, client, name) => files.ignored_names[client]?.has(name.toLowerCase()) || false;

// Regla de dueño: un update de cliente sólo va a ese cliente; los generales (ZAF) van a todos.
export const canGoTo = (file, client) => file.universal || file.owner === client.toUpperCase();
export const ownerLabel = (file) => file.universal ? 'General' : `Solo ${file.owner}`;

// Clientes elegidos a los que todavía se les puede publicar este update.
export const publishTargets = (files, file, clients) => clients.filter(c => canGoTo(file, c) && !isPublished(files, c, file.name) && !isIgnored(files, c, file.name));

// Nombre en minúsculas -> nombre real de cada update del listado. Son miles de nombres y se consulta en cada fila,
// por cada cliente elegido: se arma una sola vez por listado (y se suelta junto con él).
const lowerNamesByListing = new WeakMap();
const lowerNames = (files) => {
    if (!lowerNamesByListing.has(files)) lowerNamesByListing.set(files, Object.fromEntries(Object.keys(files.all_files_map).map(n => [n.toLowerCase(), n])));
    return lowerNamesByListing.get(files);
};

// Cadena completa (recursiva) de prerrequisitos que le faltan a alguno de los clientes, en orden de publicación.
export const missingPrerequisites = (files, file, clients) => {
    const byLower = lowerNames(files);
    const chain = [], seen = new Set();
    const visit = (name) => {
        const real = byLower[name.toLowerCase()];
        if (!real || seen.has(real)) return;
        seen.add(real);
        files.all_files_map[real].prerequisites.forEach(visit);
        if (real !== file.name && clients.some(c => !isPublished(files, c, real))) chain.push(real);
    };
    visit(file.name);
    return chain;
};

// Updates de cliente (no generales) que hay en la cadena de prerrequisitos de cada update, él incluido. Se calcula
// una vez por listado: lo consulta cada fila, por cada cliente elegido.
const ownedChains = new WeakMap();
const ownedInChain = (files, file) => {
    if (!ownedChains.has(files)) ownedChains.set(files, new Map());
    const cache = ownedChains.get(files);
    if (cache.has(file.name)) return cache.get(file.name);
    const byLower = lowerNames(files);
    const owned = [], seen = new Set(), pending = [file.name];
    while (pending.length > 0) {
        const real = byLower[pending.pop().toLowerCase()];
        if (!real || seen.has(real)) continue;
        seen.add(real);
        if (!files.all_files_map[real].universal) owned.push(real);
        pending.push(...files.all_files_map[real].prerequisites);
    }
    cache.set(file.name, owned);
    return owned;
};

// Un update no le va a llegar nunca a un cliente si en su cadena de prerrequisitos hay uno de otro cliente que
// él no tiene publicado: la regla de dueño no deja publicárselo. Es lo mismo que cuenta el servidor en "Pendientes".
export const neverReaches = (files, file, client) => ownedInChain(files, file)
    .some(name => !canGoTo(files.all_files_map[name], client) && !isPublished(files, client, name));

// Qué se puede hacer con un update disponible dados los clientes elegidos, y por qué no cuando no se puede.
// `pending` dice si el update está para liberar: se le puede llegar a publicar a alguno de los clientes que no lo tienen.
export const publishState = (files, file, clients) => {
    if (clients.length === 0) return { canPublish: false, reason: 'Elegí al menos un cliente.', targets: [], missing: [] };
    const allowed = clients.filter(c => canGoTo(file, c));
    if (allowed.length === 0) return { canPublish: false, reason: `Este update es de ${file.owner}: no se puede liberar a ${clients.join(', ')}.`, targets: [], missing: [] };
    const targets = publishTargets(files, file, clients);
    if (targets.length === 0) {
        // Cada cliente con lo suyo: decir "ignorado" de uno que lo tiene publicado es mentirle a quien lee.
        const published = allowed.filter(c => isPublished(files, c, file.name)), ignored = allowed.filter(c => !published.includes(c));
        const reason = ignored.length === 0 ? `Ya está publicado para ${published.join(', ')}.`
            : published.length === 0 ? `Está ignorado para ${ignored.join(', ')}.`
            : `Ya está publicado para ${published.join(', ')} e ignorado para ${ignored.join(', ')}.`;
        return { canPublish: false, done: ignored.length === 0, reason, targets: [], missing: [] };
    }
    // "Faltan" recorre toda la cadena de prerrequisitos y sólo lo muestran las filas que se pintan: se calcula la
    // primera vez que alguien lo pide, no para los miles de updates que coinciden con la búsqueda.
    let missing;
    return {
        canPublish: true, pending: targets.some(c => !neverReaches(files, file, c)), targets, reason: `Publicar para ${targets.join(', ')}`,
        get missing() { return missing ??= missingPrerequisites(files, file, targets); },
    };
};

// Búsqueda por nombre y, opcionalmente, por el nombre de cualquier prerrequisito (recursivo).
export const makeSearchFilter = (files, term, includePrerequisites) => {
    const search = term.trim().toLowerCase();
    if (!search) return () => true;
    const matches = (name, visited = new Set()) => {
        if (!name || visited.has(name)) return false;
        visited.add(name);
        if (name.toLowerCase().includes(search)) return true;
        return includePrerequisites && (files.all_files_map[name]?.prerequisites || []).some(p => matches(p, visited));
    };
    return (file) => matches(file.name);
};

// Mapa inverso de prerrequisitos: nombre -> updates que lo requieren directamente.
export const requiredByMap = (files) => {
    const byLower = lowerNames(files);
    const map = {};
    files.all_updates.forEach(f => f.prerequisites.forEach(p => {
        const real = byLower[p.toLowerCase()];
        if (real) (map[real] = map[real] || []).push(f.name);
    }));
    return map;
};

// A quién se le pueden ignorar los updates elegidos: los clientes a los que les corresponde alguno, con cuántos
// tienen disponibles (ni publicados ni ignorados). Lo publicado no entra: se ignora desde su fila de Publicados.
// `preselected` son los que arrancan tildados: de los clientes elegidos arriba (o de todos, si no se eligió
// ninguno), los que tienen algo disponible.
export const ignoreCandidates = (files, fileList, allClients, selectedClients) => {
    const chosen = selectedClients.length > 0 ? selectedClients : allClients;
    const candidates = allClients.map(client => {
        const applicable = fileList.filter(file => canGoTo(file, client));
        const available = applicable.filter(file => !isPublished(files, client, file.name) && !isIgnored(files, client, file.name));
        const status = available.length > 0 ? 'available' : applicable.every(file => isIgnored(files, client, file.name)) ? 'ignored' : 'published';
        return { client, applicable: applicable.length, available: available.length, status };
    }).filter(candidate => candidate.applicable > 0);
    return {
        candidates,
        preselected: candidates.filter(c => c.status === 'available' && chosen.includes(c.client)).map(c => c.client),
        notApplicable: selectedClients.filter(client => !fileList.some(file => canGoTo(file, client))),
    };
};
